import { useState, FC, forwardRef } from 'react';

import { Popover } from 'react-tiny-popover';

import SelectedComponent from '@shared/searchBar/datePicker/selectedComponent';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import {
  SearchBarInputProps,
  SearchBarItemProps,
} from '@components/ui/search/SearchBarInput/SearchBarInputInterface';
import {
  StyledSearchBarWrapper,
  RenderOverlayContentWrapper,
  SelectedComponentWrapper,
} from '@components/ui/search/SearchBarInput/SearchBarInputStyle';
import { formatShortLocalDate } from '@utils/dateUtil';
import useMountedRef from '@hooks/useMountedRef';

export const SearchBarItem: FC<SearchBarItemProps> = ({
  InputComponent,
  label,
}) => {
  const dateData = InputComponent?.toString().split('-');
  const startDate = dateData?.[0];

  return (
    <SelectedComponentWrapper>
      <SelectedComponent
        value={formatShortLocalDate(startDate) as string}
        label={label || ''}
        rightBorder={true}
      />
    </SelectedComponentWrapper>
  );
};

export interface CustomPopoverChildComponentProps
  extends React.ComponentPropsWithoutRef<'div'> {
  onClick(): void;
}

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <StyledSearchBarWrapper
    ref={ref}
    onClick={props.onClick}
    data-testid='flight-date-selection-button'
  >
    {props.children}
  </StyledSearchBarWrapper>
));

const CarRentalSearchbarInput: FC<SearchBarInputProps> = ({
  InputComponent,
  renderOverlayContent,
  error,
  label,
  popoverContentReady,
  popoverProps: {
    //defaultIsOpen and isOpen should not be set together.
    isOpen, //controlled popover
    defaultIsOpen, //un-controlled popover
    padding = 10,
    onVisibleChange,
    onClickOutside,
    containerStyle,
    ...otherPopoverProps
  } = {},
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(!!defaultIsOpen);
  const { parentRef, isParentReady } = useMountedRef<HTMLDivElement>();
  const isControlledPopover = typeof isOpen === 'boolean';

  const closeOverlay = () => {
    isControlledPopover ? onVisibleChange?.(false) : setIsOverlayOpen(false);
  };

  const toggleOverlay = () => {
    isControlledPopover
      ? onVisibleChange?.(!isOpen)
      : setIsOverlayOpen(!isOverlayOpen);
  };

  const isOverlayVisible = isControlledPopover ? !!isOpen : isOverlayOpen;
  const isReadinessEnabled = typeof popoverContentReady === 'boolean';
  const isPopoverContentVisible =
    !isReadinessEnabled || (isOverlayVisible && popoverContentReady);
  const defaultContainerStyle: Partial<CSSStyleDeclaration> = {
    zIndex: '99',
    backgroundColor: 'white',
    width: '540px',
    display: 'flex',
    justifyContent: 'center',
    margin: '0px',
    boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
    top: '-9px',
  };
  const inheritedContainerStyle = containerStyle ?? defaultContainerStyle;
  const resolvedContainerStyle = isReadinessEnabled
    ? {
        ...inheritedContainerStyle,
        opacity: isPopoverContentVisible ? '1' : '0',
        pointerEvents: isPopoverContentVisible ? 'auto' : 'none',
      }
    : inheritedContainerStyle;

  return (
    <div ref={parentRef}>
      {isParentReady && parentRef.current ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={padding}
          containerStyle={resolvedContainerStyle}
          reposition={false}
          parentElement={parentRef?.current as HTMLElement}
          content={() => {
            return renderOverlayContent ? (
              <RenderOverlayContentWrapper
                aria-hidden={
                  isReadinessEnabled ? !isPopoverContentVisible : undefined
                }
                inert={
                  isReadinessEnabled && !isPopoverContentVisible
                    ? true
                    : undefined
                }
              >
                {renderOverlayContent(closeOverlay)}
              </RenderOverlayContentWrapper>
            ) : (
              <div />
            );
          }}
          isOpen={isOverlayVisible}
          onClickOutside={(e) => {
            onClickOutside?.(e);
            closeOverlay();
          }}
          {...otherPopoverProps}
        >
          <CustomPopoverChildComponent onClick={toggleOverlay}>
            <LocationAnimationErrorBox hasError={error || false}>
              <SearchBarItem InputComponent={InputComponent} label={label} />
            </LocationAnimationErrorBox>
          </CustomPopoverChildComponent>
        </Popover>
      ) : null}
    </div>
  );
};

export default CarRentalSearchbarInput;
